import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { Vehicle } from '../types/vehicle';
import { firebaseService } from '../config/firebase';
import { authService } from '../services/authService';
import { vehicleService } from '../services/vehicleService';
import { useAuth } from './AuthContext';

interface VehicleContextType {
  selectedVehicle: Vehicle | null;
  setSelectedVehicle: (vehicle: Vehicle) => void;
  vehicles: Vehicle[];
  vehiclesLoading: boolean;
  addVehicle: (vehicle: Vehicle) => Promise<void>;
  removeVehicle: (vehicleId: string) => Promise<void>;
}

const VehicleContext = createContext<VehicleContextType | undefined>(undefined);

const COLLECTION_NAME = 'user_vehicles';

export const useVehicle = (): VehicleContextType => {
  const context = useContext(VehicleContext);
  if (!context) {
    throw new Error('useVehicle must be used within a VehicleProvider');
  }
  return context;
};

interface VehicleProviderProps {
  children: ReactNode;
}

export const VehicleProvider: React.FC<VehicleProviderProps> = ({ children }) => {
  const { currentUser, token, getFreshToken } = useAuth();
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [vehiclesLoading, setVehiclesLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!currentUser) {
      setVehiclesLoading(false);
      return; // Ensure 'currentUser' is set before running the effect
    }

    const fetchVehicles = async () => {
      setVehiclesLoading(true);
      try {
        const userVehiclesDoc = await firebaseService.getDocument(COLLECTION_NAME, currentUser.uid);
        if (userVehiclesDoc) {
          const freshToken = await getFreshToken();
          const vehiclePromises = userVehiclesDoc.vehicles.map((id: string) => 
            vehicleService.getVehicleById(id, freshToken || undefined)
          );
          const resolvedVehicles = await Promise.all(vehiclePromises);
          setVehicles(resolvedVehicles);
        } else {
          setVehicles([]);
        }
      } catch (error) {
        console.error('Error fetching vehicles:', error);
        setVehicles([]);
      } finally {
        setVehiclesLoading(false);
      }
    };

    fetchVehicles();
  }, [currentUser, token, getFreshToken]);

  useEffect(() => {
    if (!selectedVehicle && vehicles.length > 0) {
      setSelectedVehicle(vehicles[0]); // Set the first vehicle as the default selected vehicle
    }
  }, [selectedVehicle, vehicles]);
  
  const addVehicle = async (vehicle: Vehicle) => {
    try {
      const user = authService.getCurrentUser();
      
      if (!user) {
        // Preview mode - just add to local state without saving to Firebase
        console.log('Adding vehicle in preview mode:', vehicle.name);
        
        // Check if vehicle already exists locally
        if (vehicles.some(v => v.id === vehicle.id)) {
          console.warn('Vehicle already exists in preview mode.');
          setSelectedVehicle(vehicle);
          return;
        }
        
        setVehicles((prev) => [...prev, vehicle]);
        setSelectedVehicle(vehicle);
        return;
      }

      // Authenticated mode - save to Firebase
      const userVehiclesDoc = await firebaseService.getDocument(COLLECTION_NAME, user.uid);

      // Ensure the vehicle isn't already available for this user before proceeding
      if (userVehiclesDoc && userVehiclesDoc.vehicles.includes(vehicle.id)) {
        console.warn('Vehicle already exists for this user.');
        setSelectedVehicle(vehicle);
        return;
      }
      
      const updatedVehicleIds = userVehiclesDoc ? [...userVehiclesDoc.vehicles, vehicle.id] : [vehicle.id];

      await firebaseService.saveDocument(COLLECTION_NAME, user.uid, { vehicles: updatedVehicleIds });
      setVehicles((prev) => [...prev, vehicle]);
      setSelectedVehicle(vehicle); // Set the newly added vehicle as the selected vehicle
    } catch (error) {
      console.error('Error adding vehicle:', error);
    }
  };

  const removeVehicle = async (vehicleId: string) => {
    try {
      const user = authService.getCurrentUser();
      
      if (!user) {
        // Preview mode - just remove from local state
        console.log('Removing vehicle in preview mode:', vehicleId);
        setVehicles((prev) => prev.filter((v) => v.id !== vehicleId));
        if (selectedVehicle?.id === vehicleId) {
          setSelectedVehicle(vehicles.length > 1 ? vehicles.find(v => v.id !== vehicleId) || null : null);
        }
        return;
      }

      // Authenticated mode - remove from Firebase
      const userVehiclesDoc = await firebaseService.getDocument(COLLECTION_NAME, user.uid);
      if (userVehiclesDoc) {
        const updatedVehicleIds = userVehiclesDoc.vehicles.filter((id: string) => id !== vehicleId);
        await firebaseService.saveDocument(COLLECTION_NAME, user.uid, { vehicles: updatedVehicleIds });
        setVehicles((prev) => prev.filter((v) => v.id !== vehicleId));
      }
    } catch (error) {
      console.error('Error removing vehicle:', error);
    }
  };

  return (
    <VehicleContext.Provider
      value={{ selectedVehicle, setSelectedVehicle, vehicles, vehiclesLoading, addVehicle, removeVehicle }}
    >
      {children}
    </VehicleContext.Provider>
  );
};